import { create } from 'zustand';
import { AccountTask, WorkerConfig, SeedingAccount, WorkerInfo, WorkerPoolStatus, WorkerRequestResult, BrandCharacter } from '../types';
import { apiClient } from '../api/client';

interface AppState {
  // Worker
  workerUuid: string;
  workerConfig: WorkerConfig | null;
  workerInfo: WorkerInfo | null; // 🆕
  workerPoolStatus: WorkerPoolStatus | null; // 🆕 pool visibility
  
  // 🆕 Seeding Accounts
  seedingAccounts: SeedingAccount[];
  selectedAccount: SeedingAccount | null;
  lastAccountsFetch: number; // 🆕 Cache timestamp
  
  // 🆕 Brand Characters
  brandCharacters: BrandCharacter[];
  
  // Account Tasks (legacy)
  accountTasks: AccountTask[];
  selectedAccountTask: AccountTask | null;
  
  // Loading States
  isLoading: boolean;
  error: string | null;
  
  // Actions - Worker
  setWorkerUuid: (uuid: string) => void;
  loadWorkerConfig: (uuid: string) => Promise<void>;
  loadWorkerInfo: (workerUuid?: string) => Promise<void>; // 🆕
  loadWorkerPoolStatus: () => Promise<void>; // 🆕
  requestAdditionalWorker: () => Promise<WorkerRequestResult>; // 🆕
  
  // Actions - Seeding Accounts
  loadSeedingAccounts: (force?: boolean) => Promise<void>; // 🆕 Add force param
  createSeedingAccount: (account: Partial<SeedingAccount>) => Promise<void>; // 🆕
  updateSeedingAccount: (accountId: string, updates: Partial<SeedingAccount>) => Promise<void>; // 🆕
  deleteSeedingAccount: (accountId: string) => Promise<void>; // 🆕
  selectAccount: (accountId: string | null) => void; // 🆕
  
  // Actions - Brand Characters
  loadBrandCharacters: () => Promise<void>;
  
  // Actions - Account Tasks (legacy)
  loadAccountTasks: (workerUuid: string) => Promise<void>;
  selectAccountTask: (accountId: string | null) => void;
  addAccountTask: (workerUuid: string, task: AccountTask) => Promise<void>;
  updateAccountTask: (workerUuid: string, accountId: string, task: Partial<AccountTask>) => Promise<void>;
  deleteAccountTask: (workerUuid: string, accountId: string) => Promise<void>;
  
  clearError: () => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial State
  workerUuid: 'wkr-marketing-prod-001',
  workerConfig: null,
  workerInfo: null, // 🆕
  workerPoolStatus: null, // 🆕
  
  seedingAccounts: [], // 🆕
  selectedAccount: null, // 🆕
  lastAccountsFetch: 0, // 🆕 Cache timestamp
  brandCharacters: [], // 🆕
  
  accountTasks: [],
  selectedAccountTask: null,
  isLoading: false,
  error: null,

  // Actions - Worker
  setWorkerUuid: (uuid: string) => {
    set({ workerUuid: uuid });
  },

  loadWorkerConfig: async (uuid: string) => {
    set({ isLoading: true, error: null });
    try {
      const config = await apiClient.getWorkerConfig(uuid);
      set({ workerConfig: config, isLoading: false });
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to load worker config',
        isLoading: false 
      });
    }
  },

  // 🆕 Load worker info
  loadWorkerInfo: async (workerUuid?: string) => {
    set({ isLoading: true, error: null });
    try {
      const workerInfo = await apiClient.getWorkerInfo(workerUuid);
      set({ workerInfo, isLoading: false });
      
      // Auto-set workerUuid if found
      if (workerInfo) {
        set({ workerUuid: workerInfo.worker_uuid });
      }
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to load worker info',
        isLoading: false 
      });
    }
  },

  // 🆕 Load worker pool status
  loadWorkerPoolStatus: async () => {
    try {
      const workerPoolStatus = await apiClient.getWorkerPoolStatus();
      set({ workerPoolStatus });
    } catch (error: any) {
      console.error('Failed to load worker pool status:', error.message);
    }
  },

  // 🆕 Request an additional worker
  requestAdditionalWorker: async (): Promise<WorkerRequestResult> => {
    const result = await apiClient.requestAdditionalWorker();
    // Refresh pool status after successful allocation
    get().loadWorkerPoolStatus();
    return result;
  },

  // 🆕 Actions - Seeding Accounts
  loadSeedingAccounts: async (force = false) => {
    // ✅ Cache optimization: Skip fetch if data is fresh (< 60s old)
    const now = Date.now();
    const lastFetch = get().lastAccountsFetch;
    const CACHE_TTL = 60000; // 60 seconds
    
    if (!force && lastFetch && (now - lastFetch < CACHE_TTL)) {
      console.log('📦 Using cached accounts (age:', Math.round((now - lastFetch) / 1000), 'seconds)');
      return;
    }
    
    set({ isLoading: true, error: null });
    try {
      const accounts = await apiClient.getMyAccounts();
      set({ 
        seedingAccounts: accounts, 
        lastAccountsFetch: now, // ✅ Update cache timestamp
        isLoading: false 
      });
      console.log('✅ Fetched', accounts.length, 'accounts from API');
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to load accounts',
        isLoading: false 
      });
    }
  },

  createSeedingAccount: async (account: Partial<SeedingAccount>) => {
    set({ isLoading: true, error: null });
    try {
      const newAccount = await apiClient.createAccount(account);
      set(state => ({
        seedingAccounts: [...state.seedingAccounts, newAccount],
        lastAccountsFetch: Date.now(), // ✅ Update cache after mutation
        isLoading: false
      }));
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to create account',
        isLoading: false 
      });
      throw error;
    }
  },

  updateSeedingAccount: async (accountId: string, updates: Partial<SeedingAccount>) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.updateAccount(accountId, updates);
      set(state => ({
        seedingAccounts: state.seedingAccounts.map(a => 
          a.id === accountId ? { ...a, ...updates } : a
        ),
        selectedAccount: state.selectedAccount?.id === accountId 
          ? { ...state.selectedAccount, ...updates } 
          : state.selectedAccount,
        isLoading: false
      }));
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to update account',
        isLoading: false 
      });
      throw error;
    }
  },

  deleteSeedingAccount: async (accountId: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.deleteAccount(accountId);
      set(state => ({
        seedingAccounts: state.seedingAccounts.filter(a => a.id !== accountId),
        selectedAccount: state.selectedAccount?.id === accountId 
          ? null 
          : state.selectedAccount,
        isLoading: false
      }));
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to delete account',
        isLoading: false 
      });
      throw error;
    }
  },

  selectAccount: (accountId: string | null) => {
    if (!accountId) {
      set({ selectedAccount: null });
      return;
    }
    const account = get().seedingAccounts.find(a => a.id === accountId);
    set({ selectedAccount: account || null });
  },

  loadBrandCharacters: async () => {
    set({ isLoading: true, error: null });
    try {
      const brandCharacters = await apiClient.getBrandCharacters();
      set({ brandCharacters, isLoading: false });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to load brand characters',
        isLoading: false
      });
    }
  },

  // Actions - Account Tasks (legacy)
  loadAccountTasks: async (workerUuid: string) => {
    set({ isLoading: true, error: null });
    try {
      const tasks = await apiClient.listAccountTasks(workerUuid);
      set({ accountTasks: tasks, isLoading: false });
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to load account tasks',
        isLoading: false 
      });
    }
  },

  selectAccountTask: (accountId: string | null) => {
    if (!accountId) {
      set({ selectedAccountTask: null });
      return;
    }
    const task = get().accountTasks.find(t => t.account_id === accountId);
    set({ selectedAccountTask: task || null });
  },

  addAccountTask: async (workerUuid: string, task: AccountTask) => {
    set({ isLoading: true, error: null });
    try {
      const newTask = await apiClient.addAccountTask(workerUuid, task);
      set(state => ({
        accountTasks: [...state.accountTasks, newTask],
        isLoading: false
      }));
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to add account task',
        isLoading: false 
      });
      throw error;
    }
  },

  updateAccountTask: async (workerUuid: string, accountId: string, task: Partial<AccountTask>) => {
    set({ isLoading: true, error: null });
    try {
      const updatedTask = await apiClient.updateAccountTask(workerUuid, accountId, task);
      set(state => ({
        accountTasks: state.accountTasks.map(t => 
          t.account_id === accountId ? updatedTask : t
        ),
        selectedAccountTask: state.selectedAccountTask?.account_id === accountId 
          ? updatedTask 
          : state.selectedAccountTask,
        isLoading: false
      }));
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to update account task',
        isLoading: false 
      });
      throw error;
    }
  },

  deleteAccountTask: async (workerUuid: string, accountId: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.deleteAccountTask(workerUuid, accountId);
      set(state => ({
        accountTasks: state.accountTasks.filter(t => t.account_id !== accountId),
        selectedAccountTask: state.selectedAccountTask?.account_id === accountId 
          ? null 
          : state.selectedAccountTask,
        isLoading: false
      }));
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to delete account task',
        isLoading: false 
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
